import html2canvas from 'html2canvas';
import videoImg from 'assets/icons/landingPage/noVideoShape.svg';

/**
 * Convert a data URI to a Blob
 */
const dataURItoBlob = (dataURI: string): Blob => {
  const byteString = dataURI.split(',')[0].includes('base64')
    ? atob(dataURI.split(',')[1])
    : unescape(dataURI.split(',')[1]);

  const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
  const ia = new Uint8Array(byteString.length);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ia], { type: mimeString });
};

/**
 * Inline backgrounds (images, gradients, multiple layers) for html2canvas
 */
const inlineBackgrounds = (element: HTMLElement) => {
  const elements: HTMLElement[] = Array.from(
    element.querySelectorAll<HTMLElement>('*')
  );
  elements.forEach(el => {
    const style = window.getComputedStyle(el);
    const bgImage = style.getPropertyValue('background-image');
    if (bgImage && bgImage !== 'none') {
      el.style.backgroundImage = bgImage;
      el.style.backgroundSize = style.getPropertyValue('background-size');
      el.style.backgroundRepeat = style.getPropertyValue('background-repeat');
      el.style.backgroundPosition = style.getPropertyValue(
        'background-position'
      );
      el.style.backgroundAttachment = style.getPropertyValue(
        'background-attachment'
      );
    }
  });
};

/**
 * Generates a data URI from an HTML element
 */
const generateImageURIFromHTML = async (
  element: HTMLElement
): Promise<string> => {
  const canvas = await html2canvas(element, {
    allowTaint: true,
    useCORS: true,
    scale: 0.5,
    logging: false,
    onclone: (cloneDoc: Document) => {
      // 1️⃣ Inline backgrounds for all elements
      inlineBackgrounds(cloneDoc.documentElement as HTMLElement);

      // 2️⃣ Video player backgrounds
      const figureElements: HTMLElement[] = Array.from(
        cloneDoc.getElementsByTagName('figure')
      );
      figureElements.forEach(f => {
        f.style.backgroundColor = 'black';
      });

      const videoTimeElements: HTMLElement[] = Array.from(
        cloneDoc.querySelectorAll('[class*="TimeDisplay"]')
      );
      const controlsColor: string =
        videoTimeElements.length > 0
          ? window
              .getComputedStyle(videoTimeElements[0])
              .getPropertyValue('color')
          : 'white';

      const progressElements: HTMLElement[] = Array.from(
        cloneDoc.getElementsByTagName('progress')
      );
      progressElements.forEach(progress => {
        progress.style.height = `${progress.clientHeight}px`;
        progress.style.backgroundColor = controlsColor;
        progress.style.opacity = '0.25';
      });

      const volumeElements: HTMLElement[] = Array.from(
        cloneDoc.querySelectorAll('[class*="VolRange"]')
      );
      volumeElements.forEach(vol => {
        vol.style.backgroundColor = controlsColor;
      });

      // 3️⃣ Remove logos
      const logoImages: HTMLElement[] = Array.from(
        cloneDoc.getElementsByName('logoImage')
      ) as HTMLElement[];
      logoImages.forEach(logo => logo.remove());

      const smallLogo: HTMLElement | null = cloneDoc.querySelector(
        '[aria-label="Logo"]'
      );
      if (smallLogo) smallLogo.remove();

      // 4️⃣ Fix PrimaryButton text positioning
      const primaryButtons: HTMLElement[] = Array.from(
        cloneDoc.querySelectorAll('[class*="PrimaryButton__Text"]')
      );
      primaryButtons.forEach(btn => {
        btn.style.position = 'relative';
      });

      // 5️⃣ Replace embedded videos / iframes with placeholder
      const embeddedVideos: HTMLElement[] = Array.from(
        cloneDoc.querySelectorAll('[id*="slideId"]')
      );
      embeddedVideos.forEach(container => {
        const iframes: HTMLElement[] = Array.from(
          container.getElementsByTagName('iframe')
        );
        iframes.forEach(iframe => {
          iframe.style.backgroundImage = `url(${videoImg})`;
          iframe.style.backgroundSize = '100%';
          iframe.style.backgroundRepeat = 'no-repeat';
          iframe.style.backgroundPosition = 'center';
        });
      });

      return cloneDoc;
    },
  });

  return canvas.toDataURL('image/png', 1.0);
};

/**
 * Main function to generate a File from an HTML element
 */
export const generateImageFromHTML = async (
  element: HTMLElement,
  imageFileName: string
): Promise<File> => {
  const imageURI: string = await generateImageURIFromHTML(element);
  const blob: Blob = dataURItoBlob(imageURI);
  return new File([blob], `${imageFileName}.png`, { type: blob.type });
};
